import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../project/context/AuthContext';

// ===== Styled Components (same as before, trimmed for brevity) =====
const HeaderContainer = styled.header`
    width: 100%;
    background: #1e1f25;
    color: #fff;
    padding: 12px 30px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
    position: fixed;
    top: 0;
    z-index: 999;
`;

const Logo = styled.div`display:flex;align-items:center;font-size:1.6rem;font-weight:700;img{width:38px;margin-right:10px;}}`;
const Nav = styled.nav`display:flex;gap:25px;align-items:center;@media(max-width:768px){display:none;}}`;
// const NavLink = styled(Link)`color:#fff;text-decoration:none;font-size:1rem;position:relative;padding:6px 0;font-weight:500;transition:color .3s; &:hover{color:#f29f02;} &::after{content:'';position:absolute;bottom:-3px;left:0;width:0;height:2px;background:#f29f02;transition:width .3s;} &:hover::after{width:100%;}}`;

const NavLink = styled(Link)`
    color: #fff;
    text-decoration: none; /* Remove underline */
    font-size: 1rem;
    position: relative;
    padding: 6px 0;
    font-weight: 500;
    transition: color 0.3s ease;

    &:hover {
        color: #f29f02;
        text-decoration: none; /* Ensure underline never appears */
    }

    &::after {
        content: '';
        position: absolute;
        bottom: -3px;
        left: 0;
        width: 0;
        height: 2px;
        background: #f29f02;
        transition: width 0.3s ease;
    }

    &:hover::after {
        width: 100%;
    }
`;

const SubmenuLink = styled(Link)`
    display: block;
    color: #fff;
    text-decoration: none; /* Remove underline */
    padding: 8px 20px;
    font-size: 0.95rem;
    transition: all 0.15s ease;

    &:hover {
        background: #f29f02;
        color: #1e1f25;
        text-decoration: none; /* Ensure no underline on hover */
    }
`;


const DropdownWrapper = styled.div`position:relative;`;
const Submenu = styled.div`
    display: flex;
    flex-direction: column;
    position: absolute;
    top: 42px;
    left: 0;
    background: #2a2b33;
    border-radius: 8px;
    box-shadow: 0 8px 15px rgba(0,0,0,0.2);
    padding: 8px 0;
    min-width: 180px;
    opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
    pointer-events: ${({ isOpen }) => (isOpen ? 'auto' : 'none')};
    transition: opacity .18s ease, transform .18s ease;
    transform: ${({ isOpen }) => (isOpen ? 'translateY(0)' : 'translateY(-6px)')};
    z-index: 9999;
`;
// const SubmenuLink = styled(Link)`color:#fff;padding:8px 20px;text-decoration:none;font-size:.95rem;transition:all .15s; &:hover{background:#f29f02;color:#1e1f25;}}`;
const MenuToggle = styled.button`display:none;background:none;border:none;color:#fff;font-size:1.8rem;cursor:pointer;@media(max-width:768px){display:block;}}`;
const MobileNav = styled.div`display:${({ isOpen }) => (isOpen ? 'block' : 'none')};background:#1e1f25;position:absolute;top:64px;left:0;width:100%;border-top:1px solid #333;padding:15px 0;a{display:block;padding:12px 25px;color:#fff;text-decoration:none; &:hover{background:#f29f02;color:#1e1f25;}}}`;

// ===== Header Component (ref-based hover + safe timeout) =====
const Header = () => {
    const [submenuOpen, setSubmenuOpen] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const hoverRef = useRef(false);        // tracks whether pointer is inside dropdown area
    const timeoutRef = useRef(null);      // holds pending timeout id
    const wrapperRef = useRef(null);      // optional: ref to dropdown wrapper for other use
    const { auth } = useAuth();

    useEffect(() => {
        return () => {
            // cleanup timeout on unmount
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, []);

    const openSubmenu = () => {
        // clear any pending close timeout and mark hovered
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        hoverRef.current = true;
        setSubmenuOpen(true);
    };

    const scheduleCloseSubmenu = () => {
        // mark not hovered, start short delay before closing
        hoverRef.current = false;
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
            if (!hoverRef.current) setSubmenuOpen(false);
            timeoutRef.current = null;
        }, 150); // 120-200ms is smooth and prevents flicker
    };

    const toggleMenu = () => setMobileMenuOpen(prev => !prev);
    const closeMenu = () => setMobileMenuOpen(false);

    return (
        <HeaderContainer>
            <Logo>
                <img src={`/assets/icon_network.png`} alt="Logo" />
                L`<span style={{ color: '#f29f02' }}>Network</span>
            </Logo>

            {/* Desktop Navigation */}
            <Nav>
                <NavLink to="/">Home</NavLink>
                <NavLink to="/service">Service</NavLink>
                <DropdownWrapper
                    ref={wrapperRef}
                    onMouseEnter={openSubmenu}
                    onMouseLeave={scheduleCloseSubmenu}
                    onFocus={openSubmenu}    // keyboard accessibility: focus opens submenu
                    onBlur={scheduleCloseSubmenu} // blur schedules close
                >
                    <NavLink to="#" onClick={(e) => e.preventDefault()}>About ▾</NavLink>

                    <Submenu
                        isOpen={submenuOpen}
                        // also ensure submenu itself keeps hoverRef true while hovered
                        onMouseEnter={openSubmenu}
                        onMouseLeave={scheduleCloseSubmenu}
                    >
                        <SubmenuLink to="/about/company" onClick={closeMenu}>Compro</SubmenuLink>
                        <SubmenuLink to="/about/partnership" onClick={closeMenu}>Partnership</SubmenuLink>
                        <SubmenuLink to="/about/merchant" onClick={closeMenu}>Merchant B2B</SubmenuLink>
                        <SubmenuLink to="/about/client" onClick={closeMenu}>Client B2B</SubmenuLink>
                    </Submenu>
                </DropdownWrapper>
                <NavLink to="/catalog">Catalog</NavLink>
                <NavLink to="/request-app">Request App</NavLink>
                <NavLink to="/admin-login">Login</NavLink>
            </Nav>

            {/* Mobile Menu Toggle */}
            <MenuToggle onClick={toggleMenu}>
                {mobileMenuOpen ? '✖' : '☰'}
            </MenuToggle>

            {/* Mobile Navigation */}
            <MobileNav isOpen={mobileMenuOpen}>
                <Link to="/" onClick={closeMenu}>Home</Link>
                <Link to="/service" onClick={closeMenu}>Service</Link>
                <Link to="/about/company" onClick={closeMenu}>Compro</Link>
                <Link to="/about/partnership" onClick={closeMenu}>Partnership</Link>
                <Link to="/about/merchant" onClick={closeMenu}>Merchant B2B</Link>
                <Link to="/about/client" onClick={closeMenu}>Client B2B</Link>
                <Link to="/catalog" onClick={closeMenu}>Catalog</Link>
                <Link to="/request-app" onClick={closeMenu}>Request App</Link>
                <Link to="/admin-login" onClick={closeMenu}>Login</Link>
            </MobileNav>
        </HeaderContainer>
    );
};

export default Header;
